// HVAC consultation: what is sold, at what price, and the words on the page.
//
// One copy, read by three places:
//   - the page, src/pages/Consultation.jsx (/hvac-consultant)
//   - the server, api/consult-order.js, which works out the amount charged
//     from this file and never from the browser
//   - the pre-render, scripts/prerender.js, for Google
// So no imports here: the server and the pre-render evaluate this file as is.
//
// Saheb's prices (10 Oct 2026). All include GST. The whole fee comes off the
// bill when the customer buys the system from us.
//   Video call, 30 min ............ ₹99 (shown struck from ₹1,000)
//   Site visit + 1-hr consultation  ₹1,500 (struck from ₹5,000), Delhi NCR
//   Heat load calculation ......... ₹0.50 a sq ft (struck from ₹2), minimum ₹750
//   End-to-end HVAC design ........ ₹3 a sq ft homes, ₹4 offices and shops;
//                                   hotels, and sites over 4,000 sq ft, quoted
// Area-priced services can be paid online up to CONSULT_MAX_SQFT.

export const CONSULT_SLUG = 'hvac-consultant';

// Largest area that can be paid for online, in sq ft.
export const CONSULT_MAX_SQFT = 4000;

export const PROPERTY_TYPES = [
  { id: 'home', label: 'Home', long: 'Home, villa or flat' },
  { id: 'commercial', label: 'Office, shop', long: 'Office, shop, clinic or restaurant' },
  { id: 'hotel', label: 'Hotel', long: 'Hotel or large site', quote: true },
];

export const CONSULT_SERVICES = [
  {
    id: 'call',
    name: 'Video call · 30 min',
    price: 99,
    was: 1000,
    blurb: 'Ask anything: AC size, split or VRV, a quote you already have.',
    get: 'A 30-minute video call with our HVAC engineer.',
  },
  {
    id: 'visit',
    name: 'Site visit + 1-hr consultation',
    price: 1500,
    was: 5000,
    ncrOnly: true,
    blurb: 'An engineer at your site for an hour: what to install, where, and what it will cost. Delhi NCR.',
    get: 'A site visit with a one-hour, in-person consultation.',
  },
  {
    id: 'heatload',
    name: 'Heat load calculation',
    rate: 0.5,
    wasRate: 2,
    min: 750,
    blurb: 'Room-by-room heat load and the tonnage for each, as a PDF report. Minimum ₹750.',
    get: 'A room-by-room heat load report with the tonnage for each room.',
  },
  {
    id: 'design',
    name: 'End-to-end HVAC design',
    rates: { home: 3, commercial: 4 },
    byType: true,
    ncrOnly: true,
    featured: true,
    blurb: 'Site visit, heat load, layout drawing and BOQ, ready to tender or to build.',
    get: 'A site visit, the heat load, an HVAC layout drawing and a bill of quantities.',
  },
];

export function consultService(id) {
  return CONSULT_SERVICES.find((s) => s.id === id) || null;
}

export function propertyType(id) {
  return PROPERTY_TYPES.find((t) => t.id === id) || null;
}

// The rate for an area-priced service, or null when it is quoted instead.
export function consultRate(id, type) {
  const s = consultService(id);
  if (!s) return null;
  if (s.rate != null) return s.rate;
  if (s.rates) return s.rates[type] != null ? s.rates[type] : null;
  return null;
}

// The amount in whole rupees, or null when it cannot be paid online.
//   call, visit: fixed price
//   heat load:   ₹0.50 × sq ft, at least ₹750
//   design:      ₹3 (home) or ₹4 (commercial) × sq ft; hotels are quoted
// Area-priced services take 1 to CONSULT_MAX_SQFT sq ft.
export function consultPrice(id, sqft, type) {
  const s = consultService(id);
  if (!s) return null;
  if (s.price != null) return s.price;
  const a = Math.floor(Number(sqft));
  if (!Number.isFinite(a) || a < 1 || a > CONSULT_MAX_SQFT) return null;
  const rate = consultRate(id, type);
  if (rate == null) return null;
  return Math.max(s.min || 0, Math.ceil(a * rate));
}

// "₹99", "₹0.50 a sq ft", "₹3–4 a sq ft"
export function consultPriceText(s) {
  if (s.price != null) return `₹${s.price.toLocaleString('en-IN')}`;
  if (s.rate != null) return `₹${s.rate % 1 ? s.rate.toFixed(2) : s.rate} a sq ft`;
  const r = Object.values(s.rates || {});
  return `₹${Math.min(...r)}–${Math.max(...r)} a sq ft`;
}

// Shown wherever the callback is promised.
export const CONSULT_CALLBACK = 'Our engineer calls you within 24 hours to fix a time. Sundays and public holidays excluded.';

export const CONSULT_SEO = {
  title: 'HVAC Consultant in Delhi NCR | HVAC Design Consultants & Consultation Cost | Citi Cool',
  meta: 'HVAC consultants in Delhi NCR: video call ₹99, site visit with 1-hour consultation ₹1,500, heat load at ₹0.50 a sq ft, end-to-end HVAC design with drawing and BOQ from ₹3 a sq ft. GST included. Fee adjusted against your order.',
  h1: 'HVAC design and consultation, from the engineers who build it',
  eyebrow: 'HVAC DESIGN · DELHI NCR',
  intro: 'Heat load, HVAC design, drawings and BOQ from the engineers who install it. Book online from ₹99.',
};

export const CONSULT_STEPS = [
  'Pay online.',
  'We call to fix a time for the call or the site visit.',
  'Get your answer, or your heat load report, drawing and BOQ.',
];
